import React, {
  useState, useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import Card from '../card/card.component';
import ShadowBlock from '../shadow-block/shadow-block.component';
import FlexContainer from '../flex-container/flex-container.component';
import Menu from '../menu/menu.component';
import MenuItem from '../menu-item/menu-item.component';
import Tooltip from '../tooltip/tooltip.component';
import CheckAccess from '../check-access/check-access.component';
import ActionButton from '../action-button/action-button.component';
import CardHeader from '../card-header/card-header.component';
import CardContent from '../card-content/card-content.component';
import NoContentBlock from '../no-content-block/no-content-block.component';
import EditIcon from '../../icons/edit-icon';
import RemoveIcon from '../../icons/remove-icon';
import ConfigureIcon from '../../icons/configure-icon';
import CheckedIcon from '../../icons/checked-icon';
import FormatService from '../../services/format-service';
import ScreenService from '../../services/screen-service';
import useStylesWidget from './widget.styles';
import { TIMEOUT_MINIMAL } from '../../constants/system/timeouts';

const Widget: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const OPTION_INDEX_FIRST: number = 0;
  const WIDGET_TOP_MARGIN: number = 10;
  const SHADOW_LEVEL_MEDIUM: number = 1;

  const {
    isEmpty,
    isEditMode,
    isStaticHeight,
    isScrollable,
    isFullWidth,
    isFullHeight,
    withDelete,
    withNoHeader,
    withEditMode,
    withCustomizeMode,
    editPermissions,
    deletePermissions,
    defaultWidgetTitle,
    widgetName,
    widgetSettings,
    deleteDeps,
    className,
    onEditModeButtonClick,
    onDeleteButtonClick,
    onWidgetSettingsChange,
    widgetTitleComponent,
    children,
  } = props;

  const { classes } = useStylesWidget();

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);

  const fullHeightClassName: string | undefined = isFullHeight ? classes.widgetFullHeight : undefined;
  const fullWidthClassName: string | undefined = isFullWidth ? classes.widgetFullWidth : undefined;
  const noHeaderClassName: string | undefined = withNoHeader ? classes.widgetNoHeader : undefined;
  const contentFullHeightClassName: string | undefined = isFullHeight ? classes.widgetContentFullHeight : undefined;
  const contentStaticHeightClassName: string | undefined = isStaticHeight
    ? classes.widgetContentStaticHeight : undefined;
  const contentScrollableClassName: string | undefined = isScrollable ? classes.widgetContentScrollable : undefined;
  const headerCustomClassName: string | undefined = !!widgetTitleComponent ? classes.widgetHeaderCustom : undefined;
  const actionCustomClassName: string | undefined = !!widgetTitleComponent ? classes.widgetActionCustom : undefined;

  const widgetSetting: WidgetItem | undefined = (widgetSettings && widgetSettings[widgetName]) || undefined;
  const widgetTitle: string = (widgetSetting && widgetSetting.title) || defaultWidgetTitle;

  const resultEditPermissions: string[] = useMemo((): string[] => editPermissions || [], [editPermissions]);
  const resultDeletePermissions: string[] = useMemo((): string[] => deletePermissions || [], [deletePermissions]);

  const clickEditModeButton: MouseEventHandlerFunction = useCallback((): void => {
    if (withEditMode && onEditModeButtonClick) {
      onEditModeButtonClick();
    }

    if (widgetName) {
      setTimeout((): void => {
        ScreenService.scrollToElement(widgetName, undefined, WIDGET_TOP_MARGIN);
      }, TIMEOUT_MINIMAL);
    }
  }, [widgetName, withEditMode, isEditMode]);

  const clickDeleteButton: MouseEventHandlerFunction = useCallback((): void => {
    if (withDelete && onDeleteButtonClick) {
      onDeleteButtonClick();
    }
  }, [withDelete, ...(deleteDeps || [])]);

  const openConfigureWidgetMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeConfigureWidgetMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onConfigureWidgetButtonClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    openConfigureWidgetMenu(event);
  }, []);

  const changeWidgetSettings: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, settingId: number,
  ): void => {
    const selectedOption: WidgetOptionItem = widgetSettings && widgetSettings[widgetName]
      && widgetSettings[widgetName].filterOptions.find((filterOption: WidgetOptionItem): boolean => (
        filterOption.id === settingId
      ));

    if (onWidgetSettingsChange) {
      onWidgetSettingsChange(
        widgetName,
        {
          currentValue: selectedOption.name,
        },
        true,
      );
    }

    closeConfigureWidgetMenu(event);
  }, [widgetSettings]);

  return (
    <Card
      withLightShadow
      id={widgetName}
      className={FormatService.formatClassNames(
        classes.widget, fullHeightClassName, fullWidthClassName, noHeaderClassName, className,
      )}
    >
      {
        !withNoHeader && (
          <ShadowBlock shadowLevel={SHADOW_LEVEL_MEDIUM}>
            <CardHeader
              headerTitle={widgetTitle}
              className={FormatService.formatClassNames(classes.widgetHeader, headerCustomClassName)}
              actionClassName={FormatService.formatClassNames(classes.widgetAction, actionCustomClassName)}
              headerTitleClassName={classes.widgetHeaderTitle}
              headerComponent={widgetTitleComponent}
              actionComponent={(
                <FlexContainer>
                  {
                    withEditMode && !isEditMode && (
                      <CheckAccess permissions={resultEditPermissions}>
                        <Tooltip title={isEditMode ? 'View' : 'Edit'}>
                          <ActionButton withSmallPadding onClick={clickEditModeButton}>
                            <EditIcon />
                          </ActionButton>
                        </Tooltip>
                      </CheckAccess>
                    )
                  }
                  {
                    withCustomizeMode && (
                      <Fragment>
                        <Tooltip title={isEditMode ? 'View Mode' : 'Configure'}>
                          <ActionButton withSmallPadding onClick={onConfigureWidgetButtonClick}>
                            <ConfigureIcon />
                          </ActionButton>
                        </Tooltip>
                        {
                          widgetSettings && !!widgetSettings[widgetName]
                          && !!widgetSettings[widgetName].filterOptions.length && (
                            <Menu
                              isFlat
                              isBordered
                              menuAnchor={menuAnchor}
                              menuClassName={classes.widgetActionMenu}
                              onClose={closeConfigureWidgetMenu}
                            >
                              {
                                widgetSettings[widgetName].filterOptions
                                  .map((optionItem: WidgetOptionItem, optionIndex: number): ReactElement => {
                                    const isActive: boolean = widgetSettings[widgetName].currentValue
                                      ? widgetSettings[widgetName].currentValue === optionItem.name
                                      : optionIndex === OPTION_INDEX_FIRST;

                                    return (
                                      <MenuItem
                                        isActive={isActive}
                                        key={optionItem.id}
                                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                          changeWidgetSettings(event, optionItem.id);
                                        }}
                                      >
                                        {optionItem.title}
                                        {
                                          isActive && (
                                            <CheckedIcon />
                                          )
                                        }
                                      </MenuItem>
                                    );
                                  })
                              }
                            </Menu>
                          )
                        }
                      </Fragment>
                    )
                  }
                  {
                    withDelete && (
                      <CheckAccess permissions={resultDeletePermissions}>
                        <Tooltip title="Delete">
                          <ActionButton withSmallPadding onClick={clickDeleteButton}>
                            <RemoveIcon />
                          </ActionButton>
                        </Tooltip>
                      </CheckAccess>
                    )
                  }
                </FlexContainer>
              )}
            />
          </ShadowBlock>
        )
      }
      <CardContent className={classes.widgetCardContent}>
        {
          !isEmpty ? (
            <div
              className={FormatService.formatClassNames(
                classes.widgetContent, contentFullHeightClassName, contentStaticHeightClassName,
                contentScrollableClassName,
              )}
            >
              {children}
            </div>
          ) : (
            <NoContentBlock isExtended>
              No data to display
            </NoContentBlock>
          )
        }
      </CardContent>
    </Card>
  );
});

export default Widget;
